import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { MouseEventHandler, useContext } from 'react'

import { CartContext } from '@/context/CartProvider'
import { useAppSelector } from '@/hooks/reduxHooks'
import useTriggerEvents from '@/hooks/useTriggerEvents'
import { ComboType, ComboVariant } from '@/models/Combo'
import { selectCurrentVendor } from '@/store/features/locationSlice'
import CustomEventTypes from '@/utils/analytics/customEventTypes'
import { ProductListTypeEnum } from '@/utils/analytics/types'
import removeWishlist from '@/utils/api/removeWishlist'
import { getCookieValue } from '@/utils/helper'

import ButtonLabel, { VARIANT } from '../../ButtonLabel'
import HyperLocalAddRemoveButton from '../HyperLocalAddRemoveButton'

interface Props {
  btnContainerStyle?: string
  index?: number
  listType?: ProductListTypeEnum
  minQty?: number
  productVariant: ComboVariant
  maxQty?: number
  isDrawerVisible?: boolean
  isOutOfStock?: boolean
  length?: number
  onClick?: () => void
  quantity?: number
  componentName?: string
  componentId?: string | number
  isWishlist?: boolean
  product?: ComboType
  onShowSimilar?: () => void
  productName: string
}

const ComboAddToCart = ({
  btnContainerStyle,
  index,
  listType,
  minQty,
  productVariant,
  isDrawerVisible = false,
  isOutOfStock,
  length = 1,
  onClick,
  quantity: computedQuantity,
  componentName = '',
  componentId = '',
  isWishlist,
  product,
  onShowSimilar,
  productName,
}: Props) => {
  const { t } = useTranslation('products')
  const router = useRouter()

  const { comboItems, updateCart } = useContext(CartContext)
  const stopBubbling: MouseEventHandler<HTMLElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  const { trackCustomEvent } = useTriggerEvents()
  const vendorDetails = useAppSelector(selectCurrentVendor)
  const userId = getCookieValue('user_id')

  const quantity = computedQuantity ?? (comboItems[productVariant?.id] || 0)

  const disableRemove = !(typeof minQty === 'undefined' || quantity > minQty)

  const createEventParams = () => {
    return {
      ProductName: productName,
      ProductVariant: productVariant.id,
      ProductBrand: '',
      ProductPrice: productVariant.price,
      Page: router.asPath,
      VendorName: vendorDetails?.company_name,
      VendorId: vendorDetails?.id,
      Attribute: productVariant.applicable_acres,
      isCombo: true,
    }
  }
  const onAddOrRemoveFromCart = (isAdd: boolean) => {
    updateCart(
      {
        variantId: productVariant.id.toString(),
        quantity: 1,
        index,
        type: listType,
        variantPrice: productVariant.price,
        variantDiscount: productVariant.discount_amount,
        name: productName,
        brandName: '',
        catalogueId: 0,
        groupId: {},
        isCombo: true,
        parentType: 'COMBO',
      },
      isAdd,
    )
  }

  const SimilarCtaEventParams = () => {
    return {
      ProductName: product?.name,
      ScreenName: router.asPath,
      ProductCategory: '',
      ProductBrand: '',
      VendorName: vendorDetails?.company_name,
      VendorId: vendorDetails?.id,
      isCombo: true,
    }
  }

  return isOutOfStock ? (
    <button
      className={`rounded-lg py-2.5 justify-center items-center mt-2 bg-white border border-gray-30 w-full opacity-80 ${btnContainerStyle}`}
    >
      <ButtonLabel
        variant={VARIANT.TWO}
        className="font-nato-semibold text-primary-100"
        label={t('out_of_stock')}
      />
    </button>
  ) : quantity === 0 ? (
    <button
      className={` rounded-lg py-2.5 justify-center items-center mt-2 ${
        isDrawerVisible ? 'w-32' : 'w-full flex-1'
      }
        bg-primary-100 ${btnContainerStyle}`}
      onClick={(e) => {
        if (!isOutOfStock) {
          if (length > 1) {
            onClick?.()
          } else {
            onShowSimilar?.()
            isWishlist &&
              removeWishlist({
                userId: userId,
                productId: Number(product?.id),
                operation: 'MOVED_TO_CART',
              })
            isWishlist &&
              trackCustomEvent(
                CustomEventTypes.WISHLIST_PRODUCT_MOVED_TO_CART,
                JSON.stringify(SimilarCtaEventParams()),
              )
            onAddOrRemoveFromCart(true)
            const eventParam = createEventParams()
            if (eventParam.VendorId && eventParam.VendorName) {
              trackCustomEvent(
                CustomEventTypes.ADD_TO_CART,
                JSON.stringify({
                  componentId: componentId,
                  componentName: componentName,
                  ...eventParam,
                }),
              )
            }
          }
          stopBubbling(e)
        }
      }}
    >
      <ButtonLabel
        variant={VARIANT.TWO}
        className="ml-2 font-nato-semibold text-white"
        label={t('add')}
      />
    </button>
  ) : (
    <HyperLocalAddRemoveButton
      btnContainerStyle={btnContainerStyle}
      disableRemove={disableRemove}
      onAdd={() => {
        if (length > 1) onClick?.()
        else {
          const eventParam = createEventParams()
          trackCustomEvent(
            CustomEventTypes.ADD_ONE_ITEM_TO_CART,
            JSON.stringify(eventParam),
          )
          onAddOrRemoveFromCart(true)
        }
      }}
      onRemove={() => {
        if (length > 1) onClick?.()
        else {
          const eventParam = createEventParams()
          trackCustomEvent(
            CustomEventTypes.REMOVE_ONE_ITEM_FROM_CART,
            JSON.stringify(eventParam),
          )
          if (!disableRemove) {
            onAddOrRemoveFromCart(false)
          }
        }
      }}
      quantity={quantity}
      isDrawerVisible={isDrawerVisible}
    />
  )
}

export default ComboAddToCart
